import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, X } from 'lucide-react';
import { Button } from './ui/button';

interface CookieConsentBannerProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onClose: () => void;
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  isOpen,
  onAccept,
  onDecline,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleAccept = () => {
    onAccept();
  };

  const handleDecline = () => {
    onDecline();
  };

  const handleClose = () => {
    // Close is treated as decline
    onClose();
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border shadow-lg backdrop-blur-sm">
      <div className="max-w-7xl mx-auto p-4">
        <div className="flex items-center justify-between gap-4">
          {/* Cookie Icon and Content */}
          <div className="flex items-start gap-3 flex-1">
            <Shield className="h-5 w-5 mt-1 text-primary flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-sm mb-1 text-foreground">
                {t('cookies.banner.title', 'We value your privacy')}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t(
                  'cookies.banner.description',
                  'We use cookies to enhance your experience on ScreenApp, including analytics to improve our service and understand how you use our platform. By clicking "Accept", you consent to the use of all cookies.',
                )}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={handleDecline}
              variant="outline"
              size="sm"
              className="text-xs px-3 py-1 h-8"
            >
              {t('cookies.banner.decline', 'Decline')}
            </Button>
            <Button
              onClick={handleAccept}
              size="sm"
              className="text-xs px-3 py-1 h-8"
            >
              {t('cookies.banner.accept', 'Accept')}
            </Button>
            <Button
              onClick={handleClose}
              variant="ghost"
              size="sm"
              className="p-1 h-8 w-8"
              aria-label={t(
                'cookies.banner.closeBanner',
                'Close banner (treated as decline)',
              )}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Keep the original export name for backward compatibility
const CookieConsentModal = CookieConsentBanner;

export default CookieConsentModal;
